import { Component, ElementRef, inject, OnInit, ViewChild } from '@angular/core';
import { DynamicDialogConfig } from 'primeng/dynamicdialog';
import { BehaviorSubject, firstValueFrom, Subscription } from 'rxjs';
import { CustomModalComponent } from 'src/app/components/custom-modal/custom-modal.component';
import { CustomTableReport } from 'src/app/interfaces/custom-table-report/custom-table-report.interface';
import { AccountingInterfaceModel } from 'src/app/models/invoicing/accounting-interface.model';
import { ConfirmDialogService } from 'src/app/services/confirm-dialog/confirm-dialog.service';
import { ReportPrintService } from 'src/app/services/global-services/report-print.service';
import { AccountingInterfaceService } from 'src/app/services/invoicing/accounting-interface.service';
import { VoucherService } from 'src/app/services/invoicing/voucher.service';
import { ToastHandlerService } from 'src/app/services/toast/toast-handler.service';
import { SynchronizerAccountingInterfaceComponent } from '../synchronizer-accounting-interface/synchronizer-accounting-interface.component';

@Component({
  selector: 'app-accounting-interface-preview',
  templateUrl: './accounting-interface-preview.component.html',
  styleUrls: ['./accounting-interface-preview.component.scss'],
  standalone: false,
})
export class AccountingInterfacePreviewComponent implements OnInit {

  private readonly accountingInterfaceService = inject(AccountingInterfaceService);
  public readonly config = inject(DynamicDialogConfig);
  private readonly confirmDialogService = inject(ConfirmDialogService);
  private readonly reportPrintService = inject(ReportPrintService);
  private readonly toastHandlerService = inject(ToastHandlerService);
  private readonly voucherService = inject(VoucherService);

  @ViewChild('dataIntreface', { static: false }) dataIntreface!: ElementRef;
  @ViewChild('siesaSynchronizationModal')
  siesaSynchronizationModal?: CustomModalComponent;

  accountingInterfaceContract: AccountingInterfaceModel =
    new AccountingInterfaceModel();
  dataReport: CustomTableReport;
  quantityDetails: number = 0;
  subscription: Subscription;
  visibleTable: boolean = false;
  siesaSynchronization: boolean = false;
  componentSynchronizer = SynchronizerAccountingInterfaceComponent;
  inputsForSynchronizer$ = new BehaviorSubject<{
    accountingInterfaceId: number;
  }>({
    accountingInterfaceId: 0,
  });

  ngOnInit() {
    const input$ = this.config.data?.inputs$;
    if (!input$) {
      console.error('inputs$ no está definido en config.data');
      return;
    }
    this.subscription = input$?.subscribe((inputs: any) => {
      this.accountingInterfaceContract = inputs.accountingInterfaceModel;
      this.quantityDetails = inputs.quantityDetails;
      this.siesaSynchronization = inputs.siesaSynchronization;
    });

    if (this.accountingInterfaceContract.accountingInterfaceId != 0)
      this.getVouchersByAccountingInterfaceId();
  }

  ngOnDestroy() {
    this.subscription.unsubscribe();
  }

  exportContab() {
    if (!this.visibleTable) {
      this.toastHandlerService.showToast(
        'info',
        'No se encontraron datos para exportar.',
      );
      return;
    }
    const fileName = `Cervino_${this.accountingInterfaceContract.consecutive}`;
    if (this.accountingInterfaceContract.accountingInterfaceId != 0) {
      if (this.siesaSynchronization) {
        this.sendCountableMovementToSiesa();
      } else {
        this.accountingInterfaceService
          .exportAccounting(
            this.accountingInterfaceContract.accountingInterfaceId,
          )
          .subscribe((data) => {
            const downloadedFile = data;
            const a = document.createElement('a');
            a.setAttribute('style', 'display:none;');
            document.body.appendChild(a);
            a.download = fileName;
            a.href = URL.createObjectURL(downloadedFile);
            a.target = '_blank';
            a.click();
            document.body.removeChild(a);
          });
      }
    } else {
      this.toastHandlerService.showToast(
        'info',
        'Seleccione una Interfaz Contable valida.',
      );
    }
  }

  async sendCountableMovementToSiesa() {
    if (this.accountingInterfaceContract.active) {
      this.toastHandlerService.showToast(
        'warning',
        'No es posible exportar, la interfaz se encuentra abierta.',
        'Atención',
      );
      return;
    }

    this.inputsForSynchronizer$.next({
      accountingInterfaceId:
        this.accountingInterfaceContract.accountingInterfaceId,
    });
    this.siesaSynchronizationModal?.show();
  }

  printDocument() {
    if (this.visibleTable) {
      const totals: any = this.dataReport.totals;
      const totalDebits = Number(
        String(totals?.debit ?? '0').replace(/,/g, ''),
      );
      const totalCredits = Number(
        String(totals?.credit ?? '0').replace(/,/g, ''),
      );
      if (totalDebits !== totalCredits) {
        this.toastHandlerService.showToast(
          'error',
          'Error. La suma de los Débitos y Créditos son diferentes.',
          'Atención',
        );
        return;
      }
      this.reportPrintService.printTableReport(this.dataReport, true);
    } else {
      this.toastHandlerService.showToast(
        'info',
        'No se encontraron datos para imprimir.',
      );
    }
  }

  async recalculate() {
    if (this.quantityDetails == 0) {
      this.toastHandlerService.showToast(
        'info',
        'No se encontraron documentos para procesar.',
      );
      return;
    }
  
    if (await this.confirmDialogService.confirm('¿Realmente desea recalcular la interfaz?')) {
      try {
        this.dataReport = await firstValueFrom(
          this.voucherService.recalculate(this.accountingInterfaceContract.accountingInterfaceId)
        );
        this.toastHandlerService.showToast(
          'success',
          'La interfaz se recálculo exitosamente'
        );
        this.validVisibleTable(this.dataReport.data);
      } catch (error) {
        console.error('Error al eliminar la interfaz.', error);
      }
    }
  }

  async getVouchersByAccountingInterfaceId() {
    this.dataReport = await firstValueFrom(
      this.voucherService.getVouchersByAccountingInterfaceId(
        this.accountingInterfaceContract.accountingInterfaceId,
      ),
    );
    this.validVisibleTable(this.dataReport.data);
  }

  validVisibleTable(dataReport: any[]) {
    if (dataReport && dataReport.length != 0 && dataReport.length != null) {
      this.visibleTable = true;
    }
  }
}
